import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Select, DatePicker, message } from 'antd';
import Box from '@iso/components/utility/box';
import { iotDevice } from '../../../services/iotdevice.service';
import { device } from '../../../services/device.service';
import { sensorService } from '../../../services/sensor.service';
import ActivityWrapper from './Activity.styles';
import ActivityMap from './ActivityMap';
import ActivityDevices from './ActivityDevices';
import moment from 'moment-timezone';
import { commonHelper } from '@iso/lib/helpers/commonHelper';

const { Option } = Select;
const { RangePicker } = DatePicker;

const dateFormat = 'DD/MM/YYYY HH:mm:ss';
const rangePickerDateFormat = 'DD/MM/YYYY';
const defaultTimelineOption = 'last_30_days';

const Activity = () => {
  const [breadcrumbData, setBreadcrumbData] = useState([]);
  const [dataDeviceByProperty, setDataDeviceByProperty] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [selectedDeviceType, setSelectedDeviceType] = useState(null); // New state for device type
  const [loading, setLoading] = useState(false);
  const [sliderRange, setSliderRange] = useState([0, 100]);
  const [filteredBreadcrumbData, setFilteredBreadcrumbData] = useState([]);
  // Whether the points may be joined into a path. False for a WhatsApp device:
  // there is nothing between two shared points, so a line would draw a route
  // the worker never claimed to have taken.
  const [connectPoints, setConnectPoints] = useState(true);
  // Tracking sessions represented in the current points, oldest first.
  const [pointSessions, setPointSessions] = useState([]);
  const [isMounted, setIsMounted] = useState(true);
  
  const activePropertyId = useSelector(
    (state) => state.property.activePropertyId
  );
  
  // Get active property and timezone
  const activeProperty = commonHelper.getActiveProperty();
  const propertyTimezone = activeProperty?.timezone ? activeProperty.timezone : 'UTC';
  const [selectedTimezone] = useState(propertyTimezone);
  
  // Date/time state management - changed default to last 30 days
  const defaultStartDate = moment().tz(selectedTimezone).subtract(30, 'days').format(dateFormat);
  const defaultEndDate = moment().tz(selectedTimezone).format(dateFormat);
  
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [timelineOption, setTimelineOption] = useState(defaultTimelineOption);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Timeline options
  const getTimelineOptions = () => {
    return [
      { value: 'last_24_hours', label: 'Last 24 hours' },
      { value: 'today', label: 'Today' },
      { value: 'yesterday', label: 'Yesterday' },
      { value: 'last_7_days', label: 'Last 7 days' },
      { value: 'last_30_days', label: 'Last 30 days' },
      { value: 'this_month', label: 'This month' },
      { value: 'this_year', label: 'This year'},
      { value: 'custom', label: 'Custom' },
    ];
  };

  const timelineOptions = {
    last_24_hours: {
      startDate: moment().tz(selectedTimezone).subtract(23, 'hours').subtract(59, 'minutes'),
      endDate: moment().tz(selectedTimezone)
    },
    today: {
      startDate: moment().tz(selectedTimezone).startOf('day'),
      endDate: moment().tz(selectedTimezone).endOf('day')
    },
    yesterday: {
      startDate: moment().tz(selectedTimezone).subtract(1, 'days').startOf('day'),
      endDate: moment().tz(selectedTimezone).subtract(1, 'days').endOf('day')
    },
    last_7_days: {
      startDate: moment().tz(selectedTimezone).subtract(7, 'days'),
      endDate: moment().tz(selectedTimezone)
    },
    last_30_days: {
      startDate: moment().tz(selectedTimezone).subtract(30, 'days'),
      endDate: moment().tz(selectedTimezone)
    },
    this_month: {
      startDate: moment().tz(selectedTimezone).startOf('month'),
      endDate: moment().tz(selectedTimezone).endOf('month')
    },
    this_year: {
      startDate: moment().tz(selectedTimezone).startOf('year'),
      endDate: moment().tz(selectedTimezone)
    }
  };

  const onTimelineOptionChange = (value) => {
    setTimelineOption(value);

    if (value !== 'custom' && timelineOptions[value]) {
      const { startDate, endDate } = timelineOptions[value];
      setStartDate(startDate.format(dateFormat));
      setEndDate(endDate.format(dateFormat));
    }
  };

  const handleDateChange = (date, dateString) => {
    setStartDate(date[0].startOf('day').format(dateFormat));
    setEndDate(date[1].endOf('day').format(dateFormat));
  };

  const renderTimezoneSelect = () => {
    return (
      <Select
        style={{ width: 170, marginRight: 10 }}
        defaultValue={selectedTimezone}
      >
        <Option value={selectedTimezone}>{selectedTimezone}</Option>
      </Select>
    );
  };

  // Handle slider range change - now works with point indices
  const handleSliderChange = useCallback((range) => {
    setSliderRange(range);
    
    if (breadcrumbData.length === 0) {
      setFilteredBreadcrumbData([]);
      return;
    }
    
    const startIndex = range[0];
    const endIndex = range[1] + 1; // +1 because slice end is exclusive
    
    const filtered = breadcrumbData.slice(startIndex, endIndex);
    setFilteredBreadcrumbData(filtered);
  }, [breadcrumbData]);

  // Update filtered data when breadcrumb data changes
  useEffect(() => {
    if (breadcrumbData.length > 0) {
      // Reset slider to full range when new data loads
      const maxIndex = Math.max(0, breadcrumbData.length - 1);
      setSliderRange([0, maxIndex]);
      handleSliderChange([0, maxIndex]);
    } else {
      setSliderRange([0, 0]);
      setFilteredBreadcrumbData([]);
    }
  }, [breadcrumbData, handleSliderChange]);

  // Function to get all sensors across all pages
  const getAllSensorsSafe = useCallback(async () => {
    if (!activePropertyId) return [];

    let allSensors = [];
    let currentPage = 1;
    let hasMore = true;
    const pageSize = 100; // Use a larger page size to reduce API calls

    while (hasMore) {
      try {
        const params = {
          property_id: activePropertyId,
          page: currentPage,
          pageSize: pageSize,
        };

        const res = await sensorService.getList(params);
        
        if (!isMounted) return []; // prevent processing after unmount

        if (res.code === '0000') {
          const sensors = res.sensors || [];
          allSensors = [...allSensors, ...sensors];
          
          const totalPages = res.total_page || Math.ceil((res.total_item || 0) / pageSize);
          hasMore = currentPage < totalPages;
          currentPage++;
        } else {
          hasMore = false;
        }
      } catch (error) {
        if (!isMounted) return [];
        console.error('Error fetching sensors page:', currentPage, error);
        hasMore = false;
      }
    }

    return allSensors;
  }, [activePropertyId, isMounted]);

  const getDeviceByProperty = useCallback(async () => {
    if (!activePropertyId) return;
    
    setLoading(true);
    
    try {
      // Get regular IOT devices
      const deviceRes = await device.getList(activePropertyId);
      let allDevices = [];
      
      if (deviceRes.code === "0000") {
        // history_id is what the location-history endpoint is keyed on, and it
        // differs by type: an IOT device is addressed by its SUPPLIER id
        // (Traccar), a WhatsApp device by its own devices.id. Normalising it
        // here keeps that difference out of every component below.
        const iotDevices = deviceRes.devices
          .filter(device => device.type === "IOT_devices")
          .map(device => ({ ...device, history_id: device.iot_device?.device_id }));

        // Points a worker shared over WhatsApp. Listed alongside the trackers
        // because they answer the same question — where was this person — even
        // though they arrive a completely different way.
        const whatsappDevices = deviceRes.devices
          .filter(device => device.type === "whatsapp")
          .map(device => ({ ...device, history_id: device.id }));

        allDevices = [...iotDevices, ...whatsappDevices];
      }

      // Get all sensors and filter for ASSET_TRACKING
      const allSensors = await getAllSensorsSafe();
      const assetTrackingSensors = allSensors.filter(sensor => 
        sensor.part_number === 'ASSET_TRACKING' && sensor.device.terminal_id
      );

      // Convert asset tracking sensors to device format
      const assetTrackingDevices = assetTrackingSensors.map(sensor => ({
        id: sensor.id,
        history_id: sensor.device.terminal_id,
        type: "ASSET_TRACKING",
        name: sensor.name || `Asset Tracker ${sensor.device_id}`,
        iot_device: {
          device_id: sensor.device.terminal_id, // Use terminal_id as device_id
          device_name: sensor.name || `Asset Tracker ${sensor.device_id}`
        },
        // Keep sensor data for later use
        sensor_data: sensor
      }));

      // Combine all devices
      const combinedDevices = [...allDevices, ...assetTrackingDevices];
      
      if (!isMounted) return;
      
      setDataDeviceByProperty(combinedDevices);
      
      // Set first device as selected if none selected
      if (combinedDevices.length > 0 && !selectedDeviceId) {
        const firstDevice = combinedDevices[0];
        setSelectedDeviceId(firstDevice.history_id);
        setSelectedDeviceType(firstDevice.type); // Also set the device type
      }
      
    } catch (error) {
      if (!isMounted) return;
      console.error('Error loading devices:', error);
      setDataDeviceByProperty([]);
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  }, [activePropertyId, selectedDeviceId, getAllSensorsSafe, isMounted]);
  
  const getLocationHistory = useCallback((deviceId = selectedDeviceId, deviceType = selectedDeviceType) => {
    if (!deviceId || !startDate || !endDate) return;
    
    setLoading(true);
    const params = {
      device_id: deviceId,
      from: moment(startDate, dateFormat).toISOString(),
      to: moment(endDate, dateFormat).toISOString(),
    };

    // Add deviceType to params if available
    if (deviceType) {
      params.deviceType = deviceType;
    }

    iotDevice.getLocationHistory(params).then((res) => {
      if (!isMounted) return;
      
      if (res.code === "0000") {
        // The backend says whether these points may be joined. Default true so
        // every existing tracker keeps its trail; only a source that says
        // otherwise is drawn as loose pins.
        setConnectPoints(res.connect_points !== false);
        setPointSessions(Array.isArray(res.sessions) ? res.sessions : []);
        setBreadcrumbData(res.data);
        // Reset slider to show all points when new data loads
        const maxIndex = Math.max(0, res.data.length - 1);
        setSliderRange([0, maxIndex]);
        
        // Show info message if no data is returned
        if (res.data.length === 0) {
          message.info('No location data available for this timeframe. Please try extending the date range or selecting a different time period.');
        }
      } else {
        setBreadcrumbData([]);
        setSliderRange([0, 0]);
        message.info('No location data available for this timeframe. Please try extending the date range or selecting a different time period.');
      }
      setLoading(false);
    }).catch((error) => {
      if (!isMounted) return;
      console.error('Error loading location history:', error);
      setBreadcrumbData([]);
      setSliderRange([0, 0]);
      message.error('Failed to load location data. Please try again.');
      setLoading(false);
    });
  }, [selectedDeviceId, selectedDeviceType, startDate, endDate, isMounted, dataDeviceByProperty]);

  const handleDeviceSelect = (e) => {
    const deviceId = e.target.value;
    
    // Find the selected device to get its type
    const selectedDevice = dataDeviceByProperty.find(device => 
      device.history_id === deviceId
    );
    
    const deviceType = selectedDevice ? selectedDevice.type : null;
    
    setSelectedDeviceId(deviceId);
    setSelectedDeviceType(deviceType);
    
    // Clear existing data and reset slider when changing devices
    setBreadcrumbData([]);
    setFilteredBreadcrumbData([]);
    setSliderRange([0, 0]);
    
    getLocationHistory(deviceId, deviceType);
  };

  // Effect to reload data when dates change
  useEffect(() => {
    if (activePropertyId && selectedDeviceId) {
      getLocationHistory();
    }
  }, [activePropertyId, startDate, endDate, selectedDeviceId, getLocationHistory]);

  useEffect(() => {
    if (activePropertyId) {
      getDeviceByProperty();
    }
  }, [activePropertyId, getDeviceByProperty]);

  return (
    <ActivityWrapper>
        {/* Header with date/timezone controls */}
        <div className="manage-property-header">
          <div className="mp-title">
            <h2 className="manage-property-title">Activity</h2>
          </div>
          
          <div className="button-group">
            {renderTimezoneSelect()}

            <Select style={{ width: 140 }} defaultValue={timelineOption} onChange={onTimelineOptionChange}>
              {getTimelineOptions().map((option, index) => (
                <Option key={index} value={option.value}>{option.label}</Option>
              ))}
            </Select>
            
            {timelineOption === 'custom' && (
              <RangePicker
                style={{ marginLeft: '15px' }}
                format={rangePickerDateFormat}
                onChange={handleDateChange}
              />
            )}
          </div>
        </div>

        {/* Map Component */}
        <ActivityMap
          breadcrumbData={breadcrumbData}
          loading={loading}
          onSliderChange={handleSliderChange}
          sliderRange={sliderRange}
          filteredBreadcrumbData={filteredBreadcrumbData}
          connectPoints={connectPoints}
          sessions={pointSessions}
        />

        {/* Devices Component */}
        <ActivityDevices
          dataDeviceByProperty={dataDeviceByProperty}
          selectedDeviceId={selectedDeviceId}
          loading={loading}
          onDeviceSelect={handleDeviceSelect}
        />
        <br></br> {/*To keep a buffer between device table and the bottom of the page*/}
    </ActivityWrapper>
  );
};

export default Activity;